import { FC, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { CalendarContainer, ReactDatePickerProps } from 'react-datepicker';
import uniq from 'lodash/uniq';
import concat from 'lodash/concat';
import usePortal from 'react-useportal';
import { dateFormats } from 'constants/date-formats';
import { getRandomInt } from '../../utils';
import {
  InfoIcon,
  Informer,
  CalendarIcon,
  DatepickerContainer,
  Description,
  Picker,
  Message,
  PickerLabel,
} from './datepicker.styled';

interface IInformer {
  show: boolean;
  message: string;
  type: 'info' | 'error';
}

interface Props extends ReactDatePickerProps {
  // Сообщение в календаре
  informer?: IInformer;
  // Описание: "С" или "По"
  description?: 'from' | 'till';
  placeholder?: string;
}

export const Datepicker: FC<Props> = observer(
  ({ informer, description, placeholder, ...props }) => {
    const { Portal } = usePortal();
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const [isExtraClose, setIsExtraClose] = useState<boolean>(false);
    let timeoutId: NodeJS.Timeout;

    useEffect(() => {
      return () => clearTimeout(timeoutId);
    }, []);

    useEffect(() => {
      // Вынужденный ререндер календаря. Если при открытом календаре добавить в него таймселект, он не отобразится
      if (!!props?.showTimeSelect) {
        setIsExtraClose(true);
        timeoutId = setTimeout(() => {
          setIsExtraClose(false);
        }, 1);
      }
    }, [props?.showTimeSelect]);

    const identifier: string = useMemo(
      () => `datepicker-${getRandomInt(0, 999999)}`,
      []
    );

    const isShowAlert: boolean = useMemo(
      () => !isExtraClose && informer?.type === 'error' && informer.show,
      [isExtraClose, informer?.type, informer?.show]
    );

    const currentFormats: string[] = useMemo(
      () =>
        uniq(concat(props.dateFormat, dateFormats)).filter(
          (dateFormat: string | undefined) => !!dateFormat
        ) as string[],
      [props.dateFormat]
    );

    const xOffset: number = useMemo(
      () => (!description ? -15 : -39),
      [description]
    );

    const toggleCalendar = (open: boolean): void => {
      setIsOpen(open);

      if (open) {
        !!props?.onCalendarOpen && props.onCalendarOpen();
      } else {
        !!props?.onCalendarClose && props.onCalendarClose();
      }
    };

    /* https://reactdatepicker.com/ Calendar container */
    const CustomContainer = ({ className, children }: any) => {
      return (
        <CalendarContainer className={className}>
          <div>{children}</div>
          {informer?.show && (
            <Informer>
              <InfoIcon />
              <Message>{informer.message}</Message>
            </Informer>
          )}
        </CalendarContainer>
      );
    };

    return (
      <DatepickerContainer
        isOpen={isOpen || isShowAlert}
        isDisabled={props.disabled}
      >
        {!!description && (
          <Description>{description === 'from' ? 'С' : 'По'}</Description>
        )}
        <Picker
          calendarContainer={CustomContainer}
          id={identifier}
          open={isOpen || isShowAlert}
          locale={'ru'}
          placeholderText={placeholder ?? 'Выберите дату'}
          popperContainer={Portal}
          popperPlacement={'bottom-start'}
          popperModifiers={[
            {
              name: 'offset',
              options: {
                offset: [xOffset, 10],
              },
            },
          ]}
          {...props}
          dateFormat={currentFormats}
          onCalendarOpen={() => toggleCalendar(true)}
          onCalendarClose={() => toggleCalendar(false)}
        />
        <PickerLabel htmlFor={identifier}>
          <CalendarIcon />
        </PickerLabel>
      </DatepickerContainer>
    );
  }
);
